<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Appointment Management' : 'Deleted Appointments'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all appointments' : 'Restore or permanently delete appointments'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/appointments/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Appointment
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Appointments</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Appointments"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Order number, notes..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Doctor Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.doctor_id"
                    :items="doctors"
                    item-text="text"
                    item-value="value"
                    label="Doctor"
                    prepend-inner-icon="mdi-doctor"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Patient Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.patient_id"
                    :items="patients"
                    item-text="text"
                    item-value="value"
                    label="Patient"
                    prepend-inner-icon="mdi-account-heart"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="2">
                  <v-autocomplete
                    v-model="filter.status"
                    :items="statuses"
                    item-text="text"
                    item-value="value"
                    label="Status"
                    prepend-inner-icon="mdi-calendar-check"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>

              <v-row>
                <!-- Date Range Filter -->
                <v-col cols="12" md="3">
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="dateRangeText"
                        label="Date Range"
                        prepend-inner-icon="mdi-calendar"
                        outlined
                        dense
                        readonly
                        clearable
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click:clear="clearDateRange"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dateRange"
                      range
                      :min="dateRange.length === 1 ? dateRange[0] : new Date().toISOString().substr(0, 10)"
                      no-title
                      @change="applyDateRange"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Combined Appointments Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Appointments List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-calendar</v-icon>
                      {{ count }}
                      {{ count === 1 ? "Appointment" : "Appointments" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>

                    <!-- Export Button -->
                    <v-btn
                      v-if="!isDeletePage() && appointments.length > 0"
                      x-small
                      color="success"
                      outlined
                      @click="generateExcel"
                      class="px-2"
                      :loading="isExcelLoading"
                    >
                      <v-icon left x-small>mdi-microsoft-excel</v-icon>
                      Export
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading appointments..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="appointments"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Appointment ID Column -->
                <template #[`item.order_number`]="{ item }">
                  <span class="font-weight-medium text-caption">
                    {{ item.order_number || item.id }}
                  </span>
                </template>

                <!-- Doctor Column -->
                <template #[`item.doctor`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="blue">
                      <v-icon color="white" size="16">mdi-doctor</v-icon>
                    </v-avatar>
                    <span>{{ item.doctor ? item.doctor.user_name : "-" }}</span>
                  </div>
                </template>

                <!-- Patient Column -->
                <template #[`item.patient`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="28" class="mr-2" color="green">
                      <v-icon color="white" size="16">mdi-account-heart</v-icon>
                    </v-avatar>
                    <span>{{
                      item.patient ? item.patient.user_name : "-"
                    }}</span>
                  </div>
                </template>

                <!-- Appointment Time Column -->
                <template #[`item.appointment_time`]="{ item }">
                  <div class="text-caption">
                    <div v-if="item.date" class="font-weight-medium">
                      {{ new Date(item.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) }}
                    </div>
                    <div v-if="item.from_time" class="grey--text">
                      {{ item.from_time.substring(0, 5) }} – {{ item.to_time ? item.to_time.substring(0, 5) : '' }}
                    </div>
                    <span v-if="!item.date && !item.from_time">—</span>
                  </div>
                </template>

                <!-- Category Column (doctor's pathy) -->
                <template #[`item.category`]="{ item }">
                  <span class="text-caption">
                    {{ item.doctor && item.doctor.pathy ? item.doctor.pathy.description : '-' }}
                  </span>
                </template>

                <!-- Type Column (derived from is_videocall/is_audiocall/is_chat) -->
                <template #[`item.appt_type`]="{ item }">
                  <status-chip x-small :color="appointmentTypeColor(item)" :icon="appointmentTypeIcon(item)" :label="String(appointmentTypeText(item))" />
                </template>

                <!-- Duration Column -->
                <template #[`item.duration`]="{ item }">
                  <div class="text-caption">
                    {{ item.duration_minutes ? `${item.duration_minutes} min` : '-' }}
                  </div>
                </template>

                <!-- Appointment Status Column -->
                <template #[`item.status`]="{ item }">
                  <div>
                    <status-chip :color="appointmentStatusColor(item.status)" :icon="appointmentStatusIcon(item.status)" :label="String(appointmentStatusText(item.status))" />
                    <div class="text-caption grey--text mt-1" v-if="item.reschedule_count > 0">
                      Rescheduled ×{{ item.reschedule_count }}
                    </div>
                  </div>
                </template>

                <!-- Payment Column -->
                <template #[`item.payment`]="{ item }">
                  <div class="text-caption font-weight-medium">
                    {{ item.amount_paid ? `₹${item.amount_paid}` : '-' }}
                  </div>
                </template>

                <!-- Consultation Link Column -->
                <template #[`item.consultation_link`]="{ item }">
                  <v-chip
                    v-if="item.consultation"
                    x-small
                    color="teal"
                    dark
                    :to="`/consultations/${encryptIt(item.consultation.id)}`"
                    @click.stop
                  >
                    <v-icon left x-small>mdi-doctor</v-icon>
                    View
                  </v-chip>
                  <span v-else class="text-caption grey--text">-</span>
                </template>

                <!-- Created Date Column -->
                <template #[`item.created_at`]="{ item }">
                  <div class="text-caption">
                    {{ formatDate(item.created_at) }}
                  </div>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <v-menu offset-y left>
                        <template #activator="{ on, attrs }">
                          <v-btn x-small icon v-bind="attrs" v-on="on">
                            <v-icon small>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>
                        <v-list dense class="py-1">
                          <!-- View -->
                          <v-list-item :to="`/appointments/${encryptIt(item.id)}?view=1`" dense>
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-icon small color="grey darken-1">mdi-eye</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-caption">View</v-list-item-title>
                          </v-list-item>

                          <!-- Reschedule -->
                          <v-list-item
                            v-if="![2,3,4,7].includes(item.status)"
                            dense
                            @click="openRescheduleDialog(item)"
                          >
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-icon small color="purple">mdi-calendar-sync</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-caption">Reschedule</v-list-item-title>
                          </v-list-item>

                          <!-- Cancel -->
                          <v-list-item
                            v-if="![2,3,4,7].includes(item.status)"
                            dense
                            @click="openCancelDialog(item)"
                          >
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-icon small color="orange darken-2">mdi-cancel</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-caption">Cancel</v-list-item-title>
                          </v-list-item>

                          <!-- View Payment -->
                          <v-list-item dense @click="openPaymentDialog(item)">
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-icon small color="green darken-1">mdi-currency-inr</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-caption">View Payment</v-list-item-title>
                          </v-list-item>

                          <!-- View Consultation -->
                          <v-list-item
                            v-if="item.consultation"
                            dense
                            :to="`/consultations/${encryptIt(item.consultation.id)}`"
                          >
                            <v-list-item-icon class="mr-2 my-auto">
                              <v-icon small color="teal">mdi-doctor</v-icon>
                            </v-list-item-icon>
                            <v-list-item-title class="text-caption">View Consultation</v-list-item-title>
                          </v-list-item>

                        </v-list>
                      </v-menu>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Appointment</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-calendar-outline</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">
                      No Appointments Found
                    </h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No appointments match your current filters. Try adjusting your search criteria."
                          : "You haven't added any appointments yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/appointments/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Appointment
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <!-- Previous Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <!-- Page Numbers -->
                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <!-- Next Button -->
                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_appointment" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Appointment deleted successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_deleted_appointment = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_appointment" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Appointment restored successfully.</h3>
                      <br />
                      <v-btn
                        color="success"
                        @click="is_restored_appointment = false"
                      >
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Reschedule Dialog -->
      <v-dialog v-model="rescheduleDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="headline purple white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-calendar-sync</v-icon>
            Reschedule Appointment
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-body-2 grey--text mb-4">
              Select a new date and time for this appointment. Status will be updated to <strong>Rescheduled</strong>.
            </p>
            <v-text-field
              v-model="rescheduleForm.date"
              label="New Date"
              type="date"
              outlined
              dense
              prepend-inner-icon="mdi-calendar"
            ></v-text-field>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="rescheduleForm.from_time"
                  label="From Time"
                  type="time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-start"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="rescheduleForm.to_time"
                  label="To Time"
                  type="time"
                  outlined
                  dense
                  prepend-inner-icon="mdi-clock-end"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeRescheduleDialog" :disabled="isRescheduling">
              <v-icon left>mdi-close</v-icon>Cancel
            </v-btn>
            <v-btn color="purple" dark @click="confirmReschedule" :loading="isRescheduling"
              :disabled="!rescheduleForm.date || !rescheduleForm.from_time || !rescheduleForm.to_time">
              <v-icon left>mdi-calendar-sync</v-icon>Reschedule
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Cancel Dialog -->
      <v-dialog v-model="cancelDialog" max-width="480" persistent>
        <v-card>
          <v-card-title class="headline orange darken-2 white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-cancel</v-icon>
            Cancel Appointment
          </v-card-title>
          <v-card-text class="pt-4">
            <p class="text-body-2 grey--text mb-3">
              This will mark the appointment as <strong>Cancelled by User</strong>.
            </p>
            <v-textarea
              v-model="cancelReason"
              label="Cancellation Reason (optional)"
              outlined
              rows="3"
              prepend-inner-icon="mdi-text"
              auto-grow
            ></v-textarea>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text color="grey darken-1" @click="closeCancelDialog" :disabled="isCancelling">
              <v-icon left>mdi-close</v-icon>Back
            </v-btn>
            <v-btn color="orange darken-2" dark @click="confirmCancel" :loading="isCancelling">
              <v-icon left>mdi-cancel</v-icon>Confirm Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- View Payment Dialog -->
      <v-dialog v-model="paymentDialog" max-width="420">
        <v-card>
          <v-card-title class="headline green darken-1 white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-currency-inr</v-icon>
            Payment Details
          </v-card-title>
          <v-card-text class="pt-4" v-if="paymentItem">
            <v-list dense>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">Appointment ID</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-medium">
                    {{ paymentItem.order_number || paymentItem.id }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">Amount Paid</v-list-item-subtitle>
                  <v-list-item-title class="font-weight-bold text-h6 green--text">
                    {{ paymentItem.amount_paid ? `₹${paymentItem.amount_paid}` : 'Not recorded' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">Appointment Status</v-list-item-subtitle>
                  <v-list-item-title>
                    <status-chip x-small :color="appointmentStatusColor(paymentItem.status)" :label="String(appointmentStatusText(paymentItem.status))" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">Appointment Type</v-list-item-subtitle>
                  <v-list-item-title>
                    <status-chip x-small :color="appointmentTypeColor(paymentItem)" :icon="appointmentTypeIcon(paymentItem)" :label="String(appointmentTypeText(paymentItem))" />
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-subtitle class="text-caption grey--text">Date</v-list-item-subtitle>
                  <v-list-item-title>
                    {{ paymentItem.date ? new Date(paymentItem.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : '-' }}
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="closePaymentDialog">
              <v-icon left>mdi-close</v-icon>Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">
                Are you sure you want to delete this appointment?
              </h3>

              <div
                class="appointment-info-card pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-calendar</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{
                        appointmentToDelete
                          ? appointmentToDelete.order_number
                          : ""
                      }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        appointmentToDelete && appointmentToDelete.date
                          ? new Date(appointmentToDelete.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) + (appointmentToDelete.from_time ? ' · ' + appointmentToDelete.from_time.substring(0,5) + ' – ' + (appointmentToDelete.to_time || '').substring(0,5) : '')
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the appointment
                to the trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted appointments section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Appointment
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { mapGetters } from "vuex";

export default {
  name: "AppointmentsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        doctor_id: "",
        patient_id: "",
        status: "",
        is_active: "",
        show_deleted: false,
        date_from: "",
        date_to: "",
      },
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      showDeleteDialog: false,
      appointmentToDelete: null,
      isDeleting: false,
      is_deleted_appointment: false,
      restore_appointment: false,
      is_restored_appointment: false,
      rescheduleDialog: false,
      itemToReschedule: null,
      rescheduleForm: { date: "", from_time: "", to_time: "" },
      isRescheduling: false,
      cancelDialog: false,
      itemToCancel: null,
      cancelReason: "",
      isCancelling: false,
      paymentDialog: false,
      paymentItem: null,
      appointments: [],
      doctors: [
        {
          id: 0,
          text: "Select Doctor",
          value: "",
        },
      ],
      patients: [
        {
          id: 0,
          text: "Select Patient",
          value: "",
        },
      ],
      statuses: [
        { text: "All Statuses", value: "" },
        { text: "Pending", value: "0" },
        { text: "Confirmed", value: "1" },
        { text: "Completed", value: "2" },
        { text: "Cancelled by User", value: "3" },
        { text: "Cancelled by Doctor", value: "4" },
        { text: "Rescheduled", value: "5" },
        { text: "No Show", value: "6" },
        { text: "Expired", value: "7" },
      ],
      activeStatuses: [
        {
          id: 0,
          text: "Select Active Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: "1" },
        { id: "INACTIVE", text: "INACTIVE", value: "0" },
      ],
      headers: [
        { text: "Appt ID", value: "order_number", sortable: false, width: "120px", align: "center" },
        { text: "Doctor", value: "doctor", sortable: false, width: "150px" },
        { text: "Patient", value: "patient", sortable: false, width: "150px" },
        { text: "Category", value: "category", sortable: false, width: "120px" },
        { text: "Type", value: "appt_type", sortable: false, width: "100px" },
        { text: "Date & Time", value: "appointment_time", sortable: false, width: "140px" },
        { text: "Duration", value: "duration", sortable: false, width: "90px" },
        { text: "Status", value: "status", sortable: false, width: "150px" },
        { text: "Payment", value: "payment", sortable: false, width: "100px" },
        { text: "Consultation", value: "consultation_link", sortable: false, width: "110px" },
        { text: "Created", value: "created_at", sortable: false, width: "100px" },
        { text: "Actions", value: "actions", sortable: false, width: "110px", align: "center" },
      ],
      isLoading: false,
      isExcelLoading: false,
      dateMenu: false,
      dateRange: [],
      cardHeight: 600,
    };
  },

  computed: {
    ...mapGetters("auth", ["isSuperAdmin", "isAdmin"]),
    isAdminOrSuperAdmin() {
      return this.isSuperAdmin || this.isAdmin;
    },
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.doctor_id ||
        this.filter.patient_id ||
        this.filter.status ||
        this.filter.is_active ||
        this.dateRange.length > 0
      );
    },
    dateRangeText() {
      if (this.dateRange.length === 2) {
        return `${this.dateRange[0]} to ${this.dateRange[1]}`;
      }
      return "";
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    if (this.$route.query.date_from) {
      this.filter.date_from = this.$route.query.date_from;
      this.filter.date_to = this.$route.query.date_to || this.$route.query.date_from;
      this.dateRange = [this.filter.date_from, this.filter.date_to];
    }
    this.getData();
    this.getUsers();
    this.calculateCardHeight();
    window.addEventListener("resize", this.calculateCardHeight);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.calculateCardHeight);
  },

  methods: {
    appointmentStatusText(status) {
      const map = {
        0: "Pending", 1: "Confirmed", 2: "Completed",
        3: "Cancelled by User", 4: "Cancelled by Doctor",
        5: "Rescheduled", 6: "No Show", 7: "Expired",
      };
      return map[status] ?? "Unknown";
    },
    appointmentStatusColor(status) {
      const map = {
        0: "warning", 1: "primary", 2: "success",
        3: "error", 4: "deep-orange", 5: "purple",
        6: "grey darken-1", 7: "blue-grey",
      };
      return map[status] ?? "grey";
    },
    appointmentStatusIcon(status) {
      const map = {
        0: "mdi-clock-outline", 1: "mdi-check", 2: "mdi-check-circle",
        3: "mdi-account-cancel", 4: "mdi-doctor", 5: "mdi-calendar-sync",
        6: "mdi-account-off", 7: "mdi-timer-off",
      };
      return map[status] ?? "mdi-help-circle";
    },
    appointmentTypeText(item) {
      if (item.is_videocall) return "Video";
      if (item.is_audiocall) return "Audio";
      return "Chat";
    },
    appointmentTypeColor(item) {
      if (item.is_videocall) return "blue";
      if (item.is_audiocall) return "green";
      return "purple";
    },
    appointmentTypeIcon(item) {
      if (item.is_videocall) return "mdi-video";
      if (item.is_audiocall) return "mdi-phone";
      return "mdi-chat";
    },
    async getUsers() {
      await axios
        .get(`appointments/users`)
        .then((response) => {
          const users = response.data.data;

          // Reset arrays
          this.doctors = [{ id: 0, text: "Select Doctor", value: "" }];
          this.patients = [{ id: 0, text: "Select Patient", value: "" }];

          // Filter users by position - doctors and patients
          const doctorUsers = users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              user.position.name.toLowerCase().includes("doctor")
          );

          const patientUsers = users.filter(
            (user) =>
              user.position &&
              user.position.name &&
              user.position.name.toLowerCase().includes("patient")
          );

          // Populate doctors dropdown with users having doctor positions
          doctorUsers.forEach((user) => {
            this.doctors.push({
              id: user.id,
              text: user.name,
              value: user.id,
            });
          });

          // Populate patients dropdown with users having patient positions
          patientUsers.forEach((user) => {
            this.patients.push({
              id: user.id,
              text: user.name,
              value: user.id,
            });
          });

          // If no users found with specific positions, show all users in both dropdowns as fallback
          if (this.doctors.length === 1 && this.patients.length === 1) {
            users.forEach((user) => {
              this.doctors.push({
                id: user.id,
                text: user.name,
                value: user.id,
              });
              this.patients.push({
                id: user.id,
                text: user.name,
                value: user.id,
              });
            });
          }
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async getData() {
      console.log("Get Appointments Data");
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      // Ensure all filter values are properly set
      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.doctor_id = this.filter.doctor_id || "";
      this.filter.patient_id = this.filter.patient_id || "";
      this.filter.status = this.filter.status || "";
      this.filter.is_active = this.filter.is_active || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&status=" +
        this.filter.status +
        "&search_keyword=" +
        this.filter.search_keyword;

      if (this.filter.date_from) {
        query += "&date_from=" + this.filter.date_from;
        if (this.filter.date_to) {
          query += "&date_to=" + this.filter.date_to;
        }
      }

      await axios
        .get(`appointments?${query}`)
        .then((response) => {
          this.appointments = response.data.data;
          this.count = response.data.count;
          this.serialNoStarting = (this.page - 1) * this.itemsPerPage;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    showDeleteConfirmation(appointment) {
      this.appointmentToDelete = appointment;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.appointmentToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.appointmentToDelete) return;

      this.isDeleting = true;

      await axios
        .post(
          `/appointments/delete/${this.encryptIt(this.appointmentToDelete.id)}`
        )
        .then(() => {
          this.showDeleteDialog = false;
          this.appointmentToDelete = null;
          this.isDeleting = false;
          this.is_deleted_appointment = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.isDeleting = false;
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async Delete_this(id) {
      await axios
        .post(`/appointments/delete/${this.encryptIt(id)}`)
        .then(() => {
          this.is_deleted_appointment = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async Restore_this(id) {
      await axios
        .post(`/appointments/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.is_restored_appointment = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async generateExcel() {
      this.isExcelLoading = true;

      let query =
        "show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&doctor_id=" +
        this.filter.doctor_id +
        "&patient_id=" +
        this.filter.patient_id +
        "&status=" +
        this.filter.status +
        "&search_keyword=" +
        this.filter.search_keyword;

      if (this.filter.date_from) {
        query += "&date_from=" + this.filter.date_from;
        if (this.filter.date_to) {
          query += "&date_to=" + this.filter.date_to;
        }
      }

      try {
        const response = await axios.get(`/appointments/excel?${query}`, {
          responseType: "blob",
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        const contentDisposition = response.headers["content-disposition"];
        let filename = "appointments_export.xlsx";
        if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Excel export failed:", error);
      }

      this.isExcelLoading = false;
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.doctor_id = "";
      this.filter.patient_id = "";
      this.filter.status = "";
      this.filter.is_active = "";
      this.dateRange = [];
      this.filter.date_from = "";
      this.filter.date_to = "";
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.doctor_id) count++;
      if (this.filter.patient_id) count++;
      if (this.filter.status) count++;
      if (this.filter.is_active) count++;
      if (this.dateRange.length > 0) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        // Reset to current page if invalid input
        this.jumpToPage = this.page;
      }
    },

    applyDateRange() {
      if (this.dateRange.length === 2) {
        this.filter.date_from = this.dateRange[0];
        this.filter.date_to = this.dateRange[1];
        this.dateMenu = false;
        this.page = 1;
        this.getData();
      }
    },

    clearDateRange() {
      this.dateRange = [];
      this.filter.date_from = "";
      this.filter.date_to = "";
      this.page = 1;
      this.getData();
    },

    openRescheduleDialog(item) {
      this.itemToReschedule = item;
      this.rescheduleForm = {
        date: item.date ? new Date(item.date).toISOString().substr(0, 10) : "",
        from_time: item.from_time ? item.from_time.substr(0, 5) : "",
        to_time: item.to_time ? item.to_time.substr(0, 5) : "",
      };
      this.rescheduleDialog = true;
    },
    closeRescheduleDialog() {
      this.rescheduleDialog = false;
      this.itemToReschedule = null;
      this.isRescheduling = false;
    },
    async confirmReschedule() {
      if (!this.itemToReschedule) return;
      this.isRescheduling = true;
      try {
        await axios.patch(`/appointments/${this.encryptIt(this.itemToReschedule.id)}`, {
          date: this.rescheduleForm.date,
          from_time: this.rescheduleForm.from_time,
          to_time: this.rescheduleForm.to_time,
          status: 5,
        });
        this.closeRescheduleDialog();
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.isRescheduling = false;
    },
    openCancelDialog(item) {
      this.itemToCancel = item;
      this.cancelReason = "";
      this.cancelDialog = true;
    },
    closeCancelDialog() {
      this.cancelDialog = false;
      this.itemToCancel = null;
      this.cancelReason = "";
      this.isCancelling = false;
    },
    async confirmCancel() {
      if (!this.itemToCancel) return;
      this.isCancelling = true;
      try {
        await axios.patch(`/appointments/${this.encryptIt(this.itemToCancel.id)}`, {
          status: 3,
          cancellation_reason: this.cancelReason,
        });
        this.closeCancelDialog();
        this.getData();
      } catch (error) {
        console.log(error);
      }
      this.isCancelling = false;
    },
    openPaymentDialog(item) {
      this.paymentItem = item;
      this.paymentDialog = true;
    },
    closePaymentDialog() {
      this.paymentDialog = false;
      this.paymentItem = null;
    },
    calculateCardHeight() {
      const viewportHeight = window.innerHeight;
      const headerHeight = 64; // App header
      const containerPadding = 48; // Container padding
      const filterCardHeight = 140; // First card (header/filters)
      const cardHeaderHeight = 60; // Second card header
      const paginationHeight = 60; // Pagination footer

      this.cardHeight =
        viewportHeight -
        headerHeight -
        containerPadding -
        filterCardHeight -
        cardHeaderHeight -
        paginationHeight;

      if (this.cardHeight < 400) {
        this.cardHeight = 400;
      }
    },
  },
};
</script>

<style scoped>
/* Compact Layout Adjustments */
.v-container {
  max-width: 100% !important;
}

/* Enhanced Table Styling - Compact Version */
.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

/* Compact Filter Cards */
.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

/* Compact Button Styling */
.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

/* Compact Pagination */
.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

/* Compact Chip Styling */
.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

/* Compact Avatar Styling */
.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

/* Progress Enhancements */
.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

/* Responsive Design */
@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }

  .v-card-title {
    padding: 8px 12px 4px !important;
  }

  .v-card-text {
    padding: 4px 12px 8px !important;
  }

  .v-card-actions {
    padding: 4px 12px !important;
  }

  .v-card-subtitle {
    padding: 4px 12px !important;
  }
}

/* No spin arrows for number input */
.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}

/* Delete Confirmation Dialog Styling */
.appointment-info-card {
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.appointment-info-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}
</style>